import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, ArrowDownToLine, Eye, CheckCircle2, Loader2, Calendar, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function ReceivablesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: "", bank_account: "" });
  const [marking, setMarking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/receivables").then((r) => setInvoices(r.data)).finally(() => setLoading(false));
  }, []);

  const markPaid = async () => {
    if (!payForm.bank_account.trim()) {
      toast.error("Please enter the bank account");
      return;
    }
    setMarking(true);
    try {
      await api.put(`/invoices/${payDialog.invoice_id}/mark-paid`, {
        payment_date: payForm.payment_date || new Date().toISOString().split("T")[0],
        bank_account: payForm.bank_account,
      });
      setInvoices(invoices.filter((i) => i.invoice_id !== payDialog.invoice_id));
      toast.success(`Invoice ${payDialog.invoice_number} marked as paid`);
      setPayDialog(null);
      setPayForm({ payment_date: "", bank_account: "" });
    } catch {
      toast.error("Failed to mark as paid");
    } finally {
      setMarking(false);
    }
  };

  const totalReceivable = invoices.reduce((s, i) => s + (i.total || 0), 0);

  const filtered = invoices.filter((i) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return i.invoice_number?.toLowerCase().includes(q) || i.client_name?.toLowerCase().includes(q);
  });

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="receivables-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Receivables</h1>
        <p className="text-sm text-[#64748b] mt-1">
          {invoices.length} unpaid invoices &middot; Total: {formatINR(totalReceivable)}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#f59e0b]/10 flex items-center justify-center">
              <ArrowDownToLine className="h-5 w-5 text-[#f59e0b]" />
            </div>
            <div>
              <p className="text-xs text-[#64748b] font-medium">Total Receivable</p>
              <p className="text-lg font-outfit font-bold text-[#0f172a]" data-testid="total-receivable">{formatINR(totalReceivable)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-[#3b82f6]" />
            </div>
            <div>
              <p className="text-xs text-[#64748b] font-medium">Sent Invoices</p>
              <p className="text-lg font-outfit font-bold text-[#0f172a]">{invoices.filter((i) => i.status === "sent").length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#64748b]/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-[#64748b]" />
            </div>
            <div>
              <p className="text-xs text-[#64748b] font-medium">Draft Invoices</p>
              <p className="text-lg font-outfit font-bold text-[#0f172a]">{invoices.filter((i) => i.status === "draft").length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by invoice # or client..."
          data-testid="receivables-search"
          className="pl-9 h-10 border-[#e2e8f0]"
        />
      </div>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748b]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-[#10b981]/30 mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">
                {search ? "No receivables match your search." : "All invoices are paid! Nothing outstanding."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Invoice #</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Client</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Due Date</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                    <th className="text-center py-3 px-4 text-[#64748b] font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => {
                    const isOverdue = inv.due_date && new Date(inv.due_date) < new Date();
                    return (
                      <tr key={inv.invoice_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`receivable-row-${inv.invoice_id}`}>
                        <td className="py-3 px-4 font-mono text-xs text-[#4338ca] font-medium">{inv.invoice_number}</td>
                        <td className="py-3 px-4 text-[#0f172a] font-medium">{inv.client_name}</td>
                        <td className="py-3 px-4 text-[#64748b]">{inv.date}</td>
                        <td className={`py-3 px-4 ${isOverdue ? "text-red-500 font-medium" : "text-[#64748b]"}`}>
                          {inv.due_date || "-"}
                        </td>
                        <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(inv.total)}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={inv.status === "sent" ? "bg-[#3b82f6] text-white" : "bg-[#f1f5f9] text-[#64748b]"}>
                            {inv.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/invoices/${inv.invoice_id}/review`)}
                              data-testid={`view-receivable-${inv.invoice_id}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setPayDialog(inv);
                                setPayForm({ payment_date: new Date().toISOString().split("T")[0], bank_account: "" });
                              }}
                              data-testid={`mark-paid-${inv.invoice_id}`}
                              className="bg-[#10b981] hover:bg-[#059669] text-white text-xs rounded-lg h-7 px-2.5"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Paid
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Record Payment</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">
              Mark invoice {payDialog?.invoice_number} as paid
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#f8fafc] rounded-lg p-3 border border-[#e2e8f0]">
              <div className="flex justify-between text-sm">
                <span className="text-[#64748b]">Client</span>
                <span className="text-[#0f172a] font-medium">{payDialog?.client_name}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-[#64748b]">Amount</span>
                <span className="font-mono font-bold text-[#4338ca]">{formatINR(payDialog?.total)}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Payment Date
              </Label>
              <Input
                type="date"
                value={payForm.payment_date}
                onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                data-testid="payment-date-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <Landmark className="h-3.5 w-3.5" /> Bank Account *
              </Label>
              <Input
                value={payForm.bank_account}
                onChange={(e) => setPayForm({ ...payForm, bank_account: e.target.value })}
                placeholder="e.g., HDFC Savings, ICICI Current"
                data-testid="bank-account-input"
                className="mt-1.5"
              />
            </div>
            <Button
              onClick={markPaid}
              disabled={marking}
              data-testid="confirm-mark-paid-btn"
              className="w-full bg-[#10b981] hover:bg-[#059669] text-white rounded-xl"
            >
              {marking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {marking ? "Processing..." : "Confirm Payment Received"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
